import React, { useState, useEffect, useRef } from 'react';
import { useEditor } from '../contexts/EditorContext';

const EditableText = ({ 
  section, 
  field, 
  children, 
  className = '', 
  as: Component = 'div',
  placeholder = 'Click to edit...'
}) => {
  const { isEditorMode, getContent, updateContent } = useEditor();
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    // Only get content from database when in editor mode
    if (isEditorMode) {
      let fallbackValue = '';
      if (typeof children === 'string') {
        fallbackValue = children;
      } else if (children !== null && children !== undefined) {
        // Convert React elements or other types to string
        fallbackValue = String(children);
      }
      const content = getContent(section, field, fallbackValue);
      setValue(content);
    }
  }, [section, field, children, getContent, isEditorMode]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleClick = (e) => {
    if (isEditorMode && !isEditing) {
      e.preventDefault();
      e.stopPropagation();
      setIsEditing(true);
    }
  };

  const handleBlur = () => {
    setIsEditing(false);
    if (value !== getContent(section, field, children)) {
      updateContent(section, field, value, 'text');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleBlur();
    } else if (e.key === 'Escape') {
      setValue(getContent(section, field, children));
      setIsEditing(false);
    }
  };

  if (!isEditorMode) {
    // Normal mode: just render children directly (no editor processing)
    return <Component className={className}>{children}</Component>;
  }

  if (isEditing) {
    // Editing mode
    return (
      <Component className={`${className} relative`}>
        {Component === 'textarea' || (typeof children === 'string' && children.length > 50) ? (
          <textarea
            ref={inputRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            className="w-full bg-white border-2 border-blue-500 rounded px-2 py-1 outline-none resize-vertical min-h-[100px]"
            style={{ fontFamily: 'inherit', fontSize: 'inherit', fontWeight: 'inherit' }}
          />
        ) : (
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            className="w-full bg-white border-2 border-blue-500 rounded px-2 py-1 outline-none"
            style={{ fontFamily: 'inherit', fontSize: 'inherit', fontWeight: 'inherit' }}
            placeholder={placeholder}
          />
        )}
      </Component>
    );
  }

  // View mode in editor (hoverable)
  return (
    <Component
      className={`${className} cursor-pointer hover:outline hover:outline-2 hover:outline-blue-400 hover:outline-dashed hover:bg-blue-50 hover:bg-opacity-30 transition-all relative group`}
      onClick={handleClick}
    >
      {value || children}
      {isEditorMode && (
        <span className="absolute -top-6 left-0 bg-blue-600 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
          Click to edit
        </span>
      )}
    </Component>
  );
};

export default EditableText;
